/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import Typography from '@mui/material/Typography'
import { Form } from 'react-final-form'
import Button from '@mui/material/Button'
import { useParams } from 'react-router-dom'
import { InputField } from 'components/Input'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { validateRequired, validateUrl } from 'utils/validation'
import { PlatformSlug } from 'constants/consts'
import { isStudyScheduleAvailable as isStudyScheduleEnabled } from 'pagesNew/StudySchedule'
import * as classes from './styles'

export type FormValues = {
  description: string
  week8url?: string
  week8settingsTitle?: string | null
  week8settingsDescription?: string | null
  week10url?: string
  week10settingsTitle?: string | null
  week10settingsDescription?: string | null
  week12url?: string
  week12settingsTitle?: string | null
  week12settingsDescription?: string | null
}

type Props = {
  initialValues: Partial<FormValues>
  onSubmit: (values: FormValues) => Promise<void>
  onClose: () => void
}

export const SINGLE_LINK_PLATFORMS = [
  PlatformSlug.DATCrusher,
  PlatformSlug.INBDEBooster,
  PlatformSlug.NBDHEBooster,
  PlatformSlug.NDEBCrusher,
  PlatformSlug.CasperBooster,
]

const getSingleWeekLabel = (platform: PlatformSlug) => {
  if ([PlatformSlug.CasperBooster].includes(platform)) {
    return '3-Week Study Schedule'
  }

  return '8-Week Study Schedule'
}

export const StudyGuideScheduleForm: FC<Props> = ({ initialValues, onSubmit, onClose }) => {
  const { platform } = useParams()

  const isStudyScheduleAvailable = isStudyScheduleEnabled(platform as PlatformSlug)
  const isSingleWeek = SINGLE_LINK_PLATFORMS.includes(platform as PlatformSlug)
  const weeks = isSingleWeek ? [8] : [8, 10, 12]

  return (
    <section css={classes.container}>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, values }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <Typography css={classes.modalTitle} variant="title2">
              Update Study Schedule
            </Typography>
            <TextareaAutosize
              css={classes.descriptionField}
              name="description"
              validate={validateRequired}
              label="Description"
              value={values.description}
            />
            {weeks.map((week) => {
              const labelBase = isSingleWeek
                ? getSingleWeekLabel(platform as PlatformSlug)
                : `${week}-Week Study Schedule`
              return (
                <div key={week} css={classes.weekBlock}>
                  <InputField
                    fullWidth
                    name={`week${week}url`}
                    validate={validateUrl}
                    placeholder="Enter URL"
                    label={`${labelBase} URL`}
                  />
                  {isStudyScheduleAvailable && (
                    <>
                      <InputField
                        fullWidth
                        name={`week${week}settingsTitle`}
                        placeholder="Enter Title"
                        label={`${labelBase} Title`}
                      />
                      <InputField
                        fullWidth
                        name={`week${week}settingsDescription`}
                        placeholder="Enter Description"
                        label={`${labelBase} Description`}
                      />
                    </>
                  )}
                </div>
              )
            })}

            <div css={classes.actionButtons}>
              <Button type="submit" size="large" disabled={submitting}>
                Update
              </Button>
              <Button variant="outlined" color="blue" size="large" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      />
    </section>
  )
}
